// @ts-check
import { gql, useMutation, useQuery } from '@apollo/client';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import React from 'react';
import * as yup from 'yup';
import { Flex, Text } from '~/design-system';
import Notification from '../Notification';

/**
 * The props that this component needs.
 * @typedef {object} UpdateImportNameStepProps
 * @property {string} workspaceId
 * @property {string} importId
 */

const schema = yup.object().shape({
  name: yup
    .string()
    .required('Please enter a name.')
    .trim() // This is handy because when calling schema.cast() you can auto-trim the string.
    // It also checks the min length after trimming.
    .min(3, 'Please provide at least 3 characters.'),
});

/**
 * The view. The props are passed in from useMatchSupplier - all the logic resides there.
 * @param {{setFormikContext: function, onSubmit: ()=>void} & ReturnType<useUpdateImportName>} props
 * @returns
 */
export default function UpdateImportName({
  loading,
  name,
  notification,
  cancelNotification,
  setFormikContext,
  onSubmit,
}) {
  // This is important so that default value can be used for TextInput field
  if (loading) return null;
  return (
    <React.Fragment>
      <Flex
        sx={{
          justifyContent: 'center',
        }}
      >
        <Formik
          initialValues={{ name }}
          onSubmit={onSubmit}
          validationSchema={schema}
        >
          {(formikContext) => {
            setFormikContext(formikContext);
            return (
              <Form>
                <Field
                  id="name"
                  name="name"
                  sx={{ width: '330px' }}
                  autoFocus
                />
                <ErrorMessage
                  name="name"
                  component={Error}
                  sx={{ marginBottom: 1 }}
                />
              </Form>
            );
          }}
        </Formik>
      </Flex>

      {notification && (
        <Notification
          onClose={cancelNotification}
          message={notification.message}
          variant={notification.success ? 'success' : 'error'}
        />
      )}
    </React.Fragment>
  );
}

/**
 * The business logic. The route is then responsible for wiring up the returned props to the view component.
 * @param {UpdateImportNameStepProps} props
 * @returns
 */
export function useUpdateImportName({ workspaceId, importId }) {
  /** @type {[import('../Notification').Notification | undefined, React.Dispatch<React.SetStateAction<import('../Notification').Notification | undefined>>]} */
  const [notification, setNotification] = React.useState();
  const queryResult = useQuery(query, {
    variables: {
      workspaceId,
      importId,
    },
  });
  const importData = queryResult?.data?.workspace?.dataImport;
  const { name } = importData || {};

  const [isSubmitting, setIsSubmitting] = React.useState(
    /** @type {boolean} */ (false)
  );
  const [_updateDataImportName] = useMutation(UPDATE_DATA_IMPORT_NAME);
  async function updateDataImportName({ name }) {
    if (isSubmitting) return;
    setIsSubmitting(true);
    // just update when the name is changed

    try {
      const { data } = await _updateDataImportName({
        variables: {
          importId,
          name,
        },
      });
      if (!data?.result?.success) {
        setNotification({
          message: 'There was an error, please try again!',
        });
      }
      setIsSubmitting(false);
    } catch (err) {
      setNotification({
        message: 'There was an error, please try again!',
      });
      setIsSubmitting(false);
      return { error: err };
    }
  }
  return {
    updateDataImportName,
    ...queryResult,
    name,
    isSubmitting,
    notification,
    cancelNotification: () => {
      setNotification(undefined);
    },
    // Don't allow an empty input box be submitted
    isValid: true,
    // (isDirty && importName && importName.length > 0) || (!isDirty && name),
  };
}

function Error({ children, ...rest }) {
  if (!children) return null;
  return (
    <Text sx={{ color: 'red', fontSize: 'small' }} {...rest}>
      {children}
    </Text>
  );
}
UpdateImportName.fragment = gql`
  fragment UpdateImportName on DataImport {
    importId
    name
    filename
    type
  }
`;

export const UPDATE_DATA_IMPORT_NAME = gql`
  mutation updateImportData($importId: ID!, $name: String!) {
    result: DataImportUpdate(
      input: { importId: $importId, patch: { name: $name } }
    ) {
      code
      success
      message
      dataImport {
        importId
        ...UpdateImportName
      }
    }
  }
  ${UpdateImportName.fragment}
`;

const query = gql`
  query UpdateImportNamePage($workspaceId: ID!, $importId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      dataImport(importId: $importId) {
        importId
        ...UpdateImportName
      }
    }
  }
  ${UpdateImportName.fragment}
`;
